import { Icon20UsersOutline } from '@vkontakte/icons';
import browser from 'Cloud/browser';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_FREE_BIZ_SAAS_USER, IS_PAID_B2B_TARIFF, IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { renderBizPaidEditorDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { openCreateCopyOfNoneditableFileModal } from 'reactApp/components/CreateCopyOfNoneditableFileModal/helper';
import {
    ACCESS_FILE_RIGHTS_OPTIONS_BIZ,
    ACCESS_FOLDER_RIGHTS_OPTIONS_BIZ,
    FREE_BIZ_SAAS_USER_ACCESS_OPTIONS,
} from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { SharingDropdown } from 'reactApp/components/SharingWindow/Dropdown/SharingDropdown';
import {
    ACCESS_FILE_RIGHTS_NO_DOWNLOAD_OPTIONS,
    ACCESS_FILE_RIGHTS_OPTIONS,
    ACCESS_FOLDER_RIGHTS_NO_DOWNLOAD_OPTIONS,
    ACCESS_FOLDER_RIGHTS_OPTIONS,
    QA_VALUE,
    TOOLTIP_OFFSET,
} from 'reactApp/components/SharingWindow/Sharing.constants';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { AccessStatus } from 'reactApp/components/SharingWindow/Sharing.types';
import { getEditorsFor, getIsFileForCoEditing, isEditorStorage } from 'reactApp/modules/editor/editor.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFeatureEditingNoneditableFormats } from 'reactApp/modules/features/features.selectors';
import { getItemTypeName } from 'reactApp/modules/file/utils';
import {
    resetWeblinkCountDownloads,
    toggleWeblinkDownloadable,
    toggleWeblinkDownloadableRequest,
    toggleWeblinkEditableRequest,
} from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import type { DownloadLimit, PublicFile } from 'reactApp/modules/public/public.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import { AccessRights } from 'reactApp/types/Tree';
import { useDisableSeoTooltip } from 'reactApp/ui/DisableSeoTooltip/hooks/useDisableSeoTooltip';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { SharingTooltipPromo } from 'reactApp/ui/SharingTooltips/SharingTooltipPromo';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './SharingOptions.css';

interface Props {
    item: PublishItem;
    onUnpublish?: () => void;
    isReadOnly: boolean;
    isReadOnlyProperty: boolean;
    disableDownload?: boolean;
    redesign?: boolean;
    showSharingTooltipPromo?: boolean;
    downloadLimits?: DownloadLimit;
    isBizUser?: boolean;
    isDownloadable?: boolean;
}

const IS_FREE_B2B_BIZ_EDITORS_USER = config.get('FREE_B2B_BIZ_EDITORS_USER');

const getAccessRights = (isReadOnly, isFolder, isPublicReadWrite, isDownloadable, isFreeBizUser): AccessStatus => {
    if (isFreeBizUser && isPublicReadWrite) {
        return AccessStatus.ReadOnly;
    }

    return isDownloadable
        ? AccessStatus.Download
        : (isPublicReadWrite && !isReadOnly && (isFolder ? AccessStatus.Upload : AccessStatus.Edit)) || AccessStatus.ReadOnly;
};

// eslint-disable-next-line max-lines-per-function
const SharingOptionAccessRights = memo(
    // eslint-disable-next-line max-lines-per-function, complexity
    ({
        item,
        onUnpublish,
        isReadOnlyProperty,
        isReadOnly,
        disableDownload = false,
        redesign,
        showSharingTooltipPromo,
        downloadLimits,
        isBizUser,
        isDownloadable,
    }: Props) => {
        const ref = useRef<HTMLDivElement>(null);
        const dispatch = useDispatch();
        const isPhone = useSelector(EnvironmentSelectors.isPhone);
        const currentStorage = useSelector(getCurrentStorage);
        const storage = isEditorStorage(currentStorage) ? EStorageType.home : currentStorage;
        const isFolder = item?.isFolder;
        const editingNoneEditableFormatsFeature = useSelector((state: RootState) =>
            getFeatureEditingNoneditableFormats(state, { file: item as unknown as PublicFile, storage })
        );
        const convertExt = editingNoneEditableFormatsFeature?.ext || '';
        const convertedStorage = editingNoneEditableFormatsFeature?.storage;
        const isConvertFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, { ...item, ext: convertExt }));
        const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
        const isEditable = !!useSelector((state: RootState) => getEditorsFor(state, convertExt ? { ...item, ext: convertExt } : item))
            ?.length;
        const isDocumentForEdit =
            (item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER) ||
            isFileForCoEditing ||
            (isConvertFileForCoEditing && !(IS_TABLET_BROWSER || browser.isIpadOs()) && isEditable);
        const isPublic = Boolean(item?.weblink);
        const parent = useSelector((state: RootState) => getStorageItemById(state, storage, item?.parent));

        const isReadOnlyParent = parent && 'isReadOnly' in parent && parent?.isReadOnly;

        let options: Record<string, { id: string; text: string }>;
        if (isFolder && isBizUser) {
            options = ACCESS_FOLDER_RIGHTS_OPTIONS_BIZ;
        } else if (isFolder && !isBizUser) {
            options = disableDownload ? ACCESS_FOLDER_RIGHTS_NO_DOWNLOAD_OPTIONS : ACCESS_FOLDER_RIGHTS_OPTIONS;
        } else if (!isFolder && isBizUser) {
            options = !IS_PAID_B2B_TARIFF ? FREE_BIZ_SAAS_USER_ACCESS_OPTIONS : ACCESS_FILE_RIGHTS_OPTIONS_BIZ;
        } else {
            options = disableDownload ? ACCESS_FILE_RIGHTS_NO_DOWNLOAD_OPTIONS : ACCESS_FILE_RIGHTS_OPTIONS;
        }

        const isPublicReadWrite = item?.weblinkAccessRights === AccessRights.write;

        const accessRights = getAccessRights(isReadOnlyProperty, isFolder, isPublicReadWrite, isDownloadable, IS_FREE_BIZ_SAAS_USER);

        const isHidden = (isFolder && isReadOnly) || (!isFolder && (!isEditable || isReadOnlyParent || !isDocumentForEdit) && !isBizUser);

        const weblinkSeoEnabled = item?.weblinkSeoEnabled && seoEnable;

        const itemName = getItemTypeName(item);

        const { tooltip, handleShowTip } = useDisableSeoTooltip(ref, itemName, weblinkSeoEnabled);

        const renderAccessRightsDropdown = useCallback(
            (onClick): ReactElement => {
                const list = Object.values(options).map(({ id, text }) => ({
                    id,
                    text,
                    check: id === accessRights,
                    qaValue: id,
                }));

                const needOmitEdit = (!isDocumentForEdit && !isFolder) || isReadOnlyParent;
                if (needOmitEdit) {
                    const editItemIdx = list.findIndex((item) => item.id === AccessStatus.Edit);
                    list.splice(editItemIdx, 1);
                }

                return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
            },
            [accessRights, isDocumentForEdit, isFolder, isReadOnlyParent, options]
        );

        const handleAccessRightsChange = useCallback(
            (id): void => {
                if (!isPublic || accessRights === id) {
                    return;
                }

                sendGa(accessRights === AccessStatus.ReadOnly ? 'access-rights' : 'ta-access-rights');
                sendPaymentGa({
                    eventCategory: ECategoryGa.public,
                    action: 'edit-type-access',
                    type_access: id,
                    public_id: item?.weblink,
                });

                const changeDownloadableState = () => {
                    if (item.weblinkDownloadable) {
                        dispatch(toggleWeblinkDownloadable({ id: item.id }));
                    }

                    if (item.count_downloads_total) {
                        dispatch(resetWeblinkCountDownloads({ id: item.id }));
                    }
                };

                if (isConvertFileForCoEditing && convertedStorage) {
                    onUnpublish?.();
                    openCreateCopyOfNoneditableFileModal({
                        file: item as unknown as PublicFile,
                        editingNoneEditableFormats: { storage: convertedStorage, ext: convertExt },
                        fromSharePopup: true,
                    });
                    return;
                }

                if (IS_FREE_B2B_BIZ_EDITORS_USER && !isEditable && isReadOnlyProperty) {
                    renderBizPaidEditorDialog();
                    return;
                }

                if (id === AccessStatus.Download) {
                    dispatch(toggleWeblinkDownloadableRequest({ item }));
                    return;
                }

                changeDownloadableState();
                dispatch(toggleWeblinkEditableRequest({ item, id }));
            },
            [
                accessRights,
                convertExt,
                convertedStorage,
                dispatch,
                isConvertFileForCoEditing,
                isEditable,
                isPublic,
                isReadOnlyProperty,
                item,
                onUnpublish,
            ]
        );

        if (isHidden) {
            return null;
        }

        const accessItem = options[accessRights];

        return (
            <div
                className={classNames(styles.rightItem, { [styles.rightItem_disabled]: weblinkSeoEnabled })}
                data-qa-item={QA_VALUE.accessItem}
                onClick={handleShowTip}
            >
                <span className={classNames(styles.rightTitle, { [styles.tabs]: redesign })}>
                    {redesign && <Icon20UsersOutline />}
                    Пользователи могут
                </span>
                <div className={styles.dropdown} ref={ref}>
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={accessItem?.id}
                        as={SharingDropdown}
                        value={isPhone ? accessItem.text : accessItem.text.toLowerCase()}
                        content={renderAccessRightsDropdown}
                        onClickItem={handleAccessRightsChange}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.accessDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        description="Пользователи могут"
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                        noPageOffset
                        redesign={redesign}
                        disabled={weblinkSeoEnabled}
                    />
                </div>
                {isFolder && showSharingTooltipPromo && (
                    <SharingTooltipPromo isMobile={isPhone} target={ref} downloadLimits={downloadLimits} />
                )}
                {tooltip}
            </div>
        );
    }
);

SharingOptionAccessRights.displayName = 'SharingOptionAccessRights';

export default SharingOptionAccessRights;
